import { apiClient } from './client';

// ── Types (mirror MyLedger.Application.Common.Interfaces.ICompanyOnboardingService) ──

export interface OnboardingTemplateDto {
  id: string;
  templateCode: string;
  templateName: string;
  description?: string | null;
  industry: string;
  versionNo: number;
  isSystem: boolean;
  isActive: boolean;
  definition: string;      // raw JSON
  createdDate: string;
}

export interface NewCompanyInput {
  code: string;
  name: string;
  legalName?: string | null;
  baseCurrencyCode?: string;
  timeZone?: string | null;
  fiscalYearStartMonth?: number;
  adminUsername?: string | null;
  adminEmail?: string | null;
}

export interface OnboardingRequest {
  templateId: string;
  companyId?: string | null;
  company?: NewCompanyInput | null;
  steps?: string[] | null;
  externalCompanyRef?: string | null;
}

/** CREATE | UPDATE | SKIP */
export interface OnboardingItemPlan {
  kind: string;
  key: string;
  description: string;
  action: string;
}

/** BLOCKER stops Apply. WARN and INFO are advisory. */
export interface OnboardingIssue {
  severity: 'BLOCKER' | 'WARN' | 'INFO';
  stepKey: string;
  code: string;
  message: string;
}

export interface OnboardingStepPlan {
  stepKey: string;
  stepName: string;
  items: OnboardingItemPlan[];
  create: number;
  update: number;
  skip: number;
}

export interface OnboardingPlanDto {
  companyCode: string;
  companyName: string;
  companyExists: boolean;
  templateCode: string;
  steps: OnboardingStepPlan[];
  issues: OnboardingIssue[];
  totalCreate: number;
  totalUpdate: number;
  totalSkip: number;
}

export interface OnboardingStepResult {
  stepKey: string;
  stepName: string;
  status: 'OK' | 'SKIPPED' | 'FAILED';
  created: number;
  updated: number;
  skipped: number;
  messages: string[];
}

export interface OnboardingResultDto {
  runId: string;
  companyId: string;
  companyCode: string;
  status: 'COMPLETED' | 'PARTIAL' | 'FAILED';
  steps: OnboardingStepResult[];
  issues: OnboardingIssue[];
  manualFollowUps: string[];
  /** Returned once, at provisioning. Never retrievable afterwards. */
  integrationApiKey?: string | null;
  adminUsername?: string | null;
  adminPassword?: string | null;
}

export interface OnboardingRunDto {
  id: string;
  companyId: string;
  companyCode: string;
  templateCode: string;
  status: string;
  externalCompanyRef?: string | null;
  startedDate: string;
  completedDate?: string | null;
  resultJson?: string | null;
}

export interface SetupPackDto {
  id: string;
  packType: 'COA' | 'POSTING_RULES';
  packCode: string;
  packName: string;
  description?: string | null;
  versionNo: number;
  itemCount: number;
  isSystem: boolean;
}

export interface SaveOnboardingTemplateRequest {
  templateCode: string;
  templateName: string;
  description?: string | null;
  industry: string;
  definition: string;              // raw JSON
  sourceTemplateId?: string | null; // set to clone a shipped template's definition
}

const BASE = '/system/onboarding';

export const onboardingApi = {
  async templates(): Promise<OnboardingTemplateDto[]> {
    const r = await apiClient.get(`${BASE}/templates`);
    return r.data?.data ?? [];
  },

  async template(id: string): Promise<OnboardingTemplateDto> {
    const r = await apiClient.get(`${BASE}/templates/${id}`);
    return r.data?.data;
  },

  async createTemplate(req: SaveOnboardingTemplateRequest): Promise<OnboardingTemplateDto> {
    const r = await apiClient.post(`${BASE}/templates`, req);
    return r.data?.data;
  },

  async updateTemplate(id: string, req: SaveOnboardingTemplateRequest): Promise<OnboardingTemplateDto> {
    const r = await apiClient.put(`${BASE}/templates/${id}`, req);
    return r.data?.data;
  },

  async deleteTemplate(id: string): Promise<void> {
    await apiClient.delete(`${BASE}/templates/${id}`);
  },

  async packs(packType?: 'COA' | 'POSTING_RULES'): Promise<SetupPackDto[]> {
    const q = packType ? `?packType=${packType}` : '';
    const r = await apiClient.get(`${BASE}/packs${q}`);
    return r.data?.data ?? [];
  },

  /** Dry run. Writes nothing — safe to call as often as the operator edits the form. */
  async preview(req: OnboardingRequest): Promise<OnboardingPlanDto> {
    const r = await apiClient.post(`${BASE}/preview`, req);
    return r.data?.data;
  },

  /** Provisions for real. `confirmation` must equal the company code. */
  async apply(request: OnboardingRequest, confirmation: string): Promise<OnboardingResultDto> {
    const r = await apiClient.post(`${BASE}/apply`, { request, confirmation });
    return r.data?.data;
  },

  async runs(): Promise<OnboardingRunDto[]> {
    const r = await apiClient.get(`${BASE}/runs`);
    return r.data?.data ?? [];
  },
};
